import { ReactNode, useState } from "react";
import { TopNavigation } from "@/components/navigation/TopNavigation";
import { BottomNavigation } from "@/components/navigation/BottomNavigation";
import { GlobalSearch } from "@/components/navigation/GlobalSearch";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { TranscriptionProvider } from "@/contexts/TranscriptionContext";
import { MobileHeaderProvider } from "@/contexts/MobileHeaderContext";
import { ConsultationTranscriptionDialog } from "@/components/medical-records/ConsultationTranscriptionDialog";
import { useLocation } from "react-router-dom";
import { AccountMenuItems } from "@/components/navigation/AccountMenuItems";

interface ModernAppLayoutProps {
  children: ReactNode;
}

export function ModernAppLayout({ children }: ModernAppLayoutProps) {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isContentScrolled, setIsContentScrolled] = useState(false);
  const location = useLocation();
  const isFlowboard = location.pathname === '/calendar/flowboard';
  const isGrooming = location.pathname === '/grooming';
  const isPatientProfile =
    /^\/patients\/pets\/[^/]+(?:\/(?:overview|history|upcoming|packages|financial|basicas|historico|futuro|pacotes|financeiro|consultas))?$/.test(location.pathname) ||
    /^\/patients\/owners\/[^/]+(?:\/(?:overview|history|upcoming|packages|financial|dados|historico|futuro|pacotes|financeiro))?$/.test(location.pathname);
  const isMedicalRecordMobileRoute =
    /^\/patients\/pets\/[^/]+\/medical-records\/new(?:\/[^/]+)?$/.test(location.pathname) ||
    /^\/medical-records\/[^/]+(?:\/edit)?(?:\/[^/]+)?$/.test(location.pathname);

  const accountMenuItems = (
    <AccountMenuItems onSelect={() => setIsMobileMenuOpen(false)} />
  );

  return (
    <TranscriptionProvider>
      <MobileHeaderProvider scrolled={isContentScrolled}>
        <div className="relative flex h-screen flex-col overflow-hidden bg-background-app">
          {/* Top Navigation */}
          <TopNavigation
            onSearchClick={() => setIsSearchOpen(true)}
          />

          {/* Main Content */}
          <main
            className="app-scroll min-h-0 w-full flex-1 overflow-y-auto"
            onScroll={(e) => {
              const scrollTop = e.currentTarget.scrollTop;
              setIsContentScrolled((wasScrolled) => (
                wasScrolled ? scrollTop > 16 : scrollTop > 72
              ));
            }}
          >
            <div
              className={
                isFlowboard || isGrooming
                  ? "mx-auto px-0 pt-2 md:pt-10 pb-24 md:pb-6"
                  : isPatientProfile || isMedicalRecordMobileRoute
                    ? "mx-auto px-0 pt-0 pb-24 md:container md:px-10 md:pt-10 md:pb-6 lg:px-20"
                    : "mx-auto px-5 pt-2 pb-24 md:container md:px-10 md:pt-10 md:pb-6 lg:px-20"
              }
            >
              {children}
            </div>
          </main>

          {/* Bottom Navigation (Mobile only) */}
          <BottomNavigation />

          {/* Search Modal */}
          <Dialog open={isSearchOpen} onOpenChange={setIsSearchOpen}>
            <DialogContent
              hideClose
              overlayClassName="bg-white/80 backdrop-blur-sm"
              className="max-w-3xl max-h-[60vh] p-0 flex flex-col overflow-hidden"
            >
              <GlobalSearch onClose={() => setIsSearchOpen(false)} />
            </DialogContent>
          </Dialog>

          {/* Mobile Menu Sheet */}
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetContent side="left" className="w-full max-w-sm">
              <div className="mt-6">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-primary">akuna</h2>
                </div>
                {accountMenuItems}
              </div>
            </SheetContent>
          </Sheet>

          {/* Transcription Dialog */}
          <ConsultationTranscriptionDialog />
        </div>
      </MobileHeaderProvider>
    </TranscriptionProvider>
  );
}
